import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  getDoc,
  doc, 
  updateDoc, 
  addDoc, 
  setDoc, 
  onSnapshot 
} from "firebase/firestore";
import { User, Mail, Shield, Save, Loader2, Upload, Check, Clock, CheckCircle, ChevronDown, ChevronUp, Download, Bell, AlertTriangle, KeyRound, RotateCcw } from "lucide-react";

import TicketChat from "../components/ui/TicketChat";
import { useNotifications } from "../hooks/useNotifications";
import MFAEnrollmentModal from "../components/modals/MFAEnrollmentModal";
import { isTicketNotification, extractTicketInfo, getTicketNavigationPath } from "../utils/ticketNotificationUtils";
import { logSecurityEvent } from "../services/AuditLogger";


export default function Profile() {
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [userDocId, setUserDocId] = useState("");
  const [activeTab, setActiveTab] = useState("info");
  const [userTickets, setUserTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [expandedTicketId, setExpandedTicketId] = useState(null);
  const [isMfaEnabled, setIsMfaEnabled] = useState(false);
  const [mfaModalOpen, setMfaModalOpen] = useState(false);
  const { getVisibleNotifications, markAsRead } = useNotifications(currentUser);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    photoURL: "",
    roles: [],
    theme: "dark"
  });

  useEffect(() => {
    if (!currentUser || !currentUser.email) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const q = query(collection(db, "users"), where("email", "==", currentUser.email));
    
    const capitalizeName = (name) => {
      if (!name) return "";
      return name.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    };

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      try {
        if (!querySnapshot.empty) {
          const userDoc = querySnapshot.docs[0];
          setUserDocId(userDoc.id);
          const data = userDoc.data();
          setIsMfaEnabled(data.mfaEnabled === true);
          setFormData(prev => ({
            name: prev.name && prev.name !== "" ? prev.name : capitalizeName(data.name || ""),
            email: data.email || currentUser.email,
            photoURL: data.photoURL || prev.photoURL || "",
            roles: data.roles || [],
            theme: data.theme || "dark"
          }));
        } else {
          setIsMfaEnabled(false);
          setFormData(prev => ({
            name: prev.name && prev.name !== "" ? prev.name : capitalizeName(currentUser.email.split("@")[0]),
            email: currentUser.email,
            photoURL: prev.photoURL || "",
            roles: [],
            theme: "dark"
          }));
        }
        setError("");
      } catch (err) {
        console.error("Error processing user profile snapshot:", err);
        setError("Failed to load user profile information.");
      } finally {
        setLoading(false);
      }
    }, (err) => {
      console.error("Error subscribing to user profile:", err);
      setError("Failed to load user profile information.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (activeTab === "tickets" && formData.name) {
      setLoadingTickets(true);
      const q = query(
        collection(db, "tickets"),
        where("createdBy", "==", formData.name)
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const list = [];
        snapshot.forEach((doc) => {
          list.push({ id: doc.id, ...doc.data() });
        });
        // Sort by createdAt descending
        list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setUserTickets(list);
        setLoadingTickets(false);
      }, (err) => {
        console.error("Error loading tickets:", err);
        setLoadingTickets(false);
      });
      return () => unsubscribe();
    }
  }, [activeTab, formData.name]);

  // Handle tab switching from URL search params (e.g. ?tab=tickets)
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "tickets" && activeTab !== "tickets") {
      setActiveTab("tickets");
    } else if (tabParam === "notifications" && activeTab !== "notifications") {
      setActiveTab("notifications");
    }
  }, [searchParams]);

  // Handle ticket auto-expansion and scrolling (?ticketId=... or ?ticketNumber=...)
  useEffect(() => {
    const targetId = searchParams.get("ticketId");
    const targetNum = searchParams.get("ticketNumber");
    if (!targetId && !targetNum) return;

    if (activeTab === "tickets") {
      const matched = userTickets.find(t => 
        (targetId && t.id === targetId) || 
        (targetNum && t.ticketNumber && t.ticketNumber.toUpperCase() === targetNum.toUpperCase())
      );

      if (matched) {
        setExpandedTicketId(matched.id);
        if (matched.lastMessageAt && currentUser && !(matched.lastMessageReadBy || []).includes(currentUser.uid)) {
          updateDoc(doc(db, "tickets", matched.id), {
            lastMessageReadBy: [...(matched.lastMessageReadBy || []), currentUser.uid]
          }).catch(console.error);
        }
        setTimeout(() => {
          const el = document.getElementById(`ticket-${matched.id}`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
      } else if (targetId && !loadingTickets) {
        // Check if user has Developer or Admin privileges to view another user's ticket
        const isStaff = (formData.roles || []).some(r => ["developer", "admin"].includes(String(r).toLowerCase()));
        if (isStaff) {
          getDoc(doc(db, "tickets", targetId)).then((snap) => {
            if (snap.exists()) {
              const ticketData = { id: snap.id, ...snap.data() };
              setUserTickets(prev => {
                if (prev.some(t => t.id === ticketData.id)) return prev;
                return [ticketData, ...prev];
              });
              setExpandedTicketId(ticketData.id);
              setTimeout(() => {
                const el = document.getElementById(`ticket-${ticketData.id}`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
              }, 200);
            }
          }).catch(console.error);
        }
      }
    }
  }, [searchParams, activeTab, userTickets, loadingTickets, formData.roles, currentUser]);

  const getTicketStatusStyle = (status) => {
    switch (status) {
      case "Complete":
        return {
          backgroundColor: "rgba(76, 175, 80, 0.15)",
          color: "var(--success)",
          border: "1px solid var(--success)"
        };
      case "Waiting":
        return {
          backgroundColor: "rgba(255, 152, 0, 0.15)",
          color: "#ff9800",
          border: "1px solid #ff9800"
        };
      case "Responded":
        return {
          backgroundColor: "rgba(33, 150, 243, 0.15)",
          color: "#2196f3",
          border: "1px solid #2196f3"
        };
      case "Open":
      default:
        return {
          backgroundColor: "rgba(239, 68, 68, 0.15)",
          color: "#ef4444",
          border: "1px solid #ef4444"
        };
    }
  };

  const handleToggleTicket = async (ticket) => {
    const isOpen = expandedTicketId === ticket.id;
    if (!isOpen) {
      setExpandedTicketId(ticket.id);
      // Clear unread flag if active user has not read last message
      if (ticket.lastMessageAt && currentUser && !(ticket.lastMessageReadBy || []).includes(currentUser.uid)) {
        try {
          await updateDoc(doc(db, "tickets", ticket.id), {
            lastMessageReadBy: [...(ticket.lastMessageReadBy || []), currentUser.uid]
          });
        } catch (err) {
          console.error("Error updating lastMessageReadBy in Profile:", err);
        }
      }
    } else {
      setExpandedTicketId(null);
    }
  };

  const handleReopenTicket = async (ticket) => {
    try {
      await updateDoc(doc(db, "tickets", ticket.id), {
        status: "Open",
        reopenedAt: new Date().toISOString(),
        reopenedBy: formData.name || currentUser?.email || "User",
        lastAction: "Reopened by User"
      });

      // Add real-time notification to developers
      await addDoc(collection(db, "notifications"), {
        key: `ticket_reopened_dev_${ticket.id}_${Date.now()}`,
        message: `User ${formData.name || "User"} reopened ticket ${ticket.ticketNumber} (${ticket.type}).`,
        type: "ticket_reopened",
        ticketId: ticket.id,
        ticketNumber: ticket.ticketNumber,
        path: `/support?ticketId=${ticket.id}&ticketNumber=${ticket.ticketNumber}`,
        targetRole: "Developer",
        createdAt: new Date().toISOString(),
        readBy: []
      });

      // Compliance audit trail
      await logSecurityEvent({
        action: "TICKET_REOPENED",
        category: "DATA_MODIFICATION",
        userId: currentUser?.uid,
        userEmail: currentUser?.email,
        resource: "tickets",
        details: {
          ticketId: ticket.id,
          ticketNumber: ticket.ticketNumber,
          previousStatus: ticket.status,
          reopenedBy: formData.name || currentUser?.email
        },
        severity: "INFO"
      });

      setSuccess(`Ticket ${ticket.ticketNumber} reopened back to Open.`);
      setTimeout(() => setSuccess(""), 4000);
    } catch (err) {
      console.error("Error reopening ticket:", err);
      setError("Failed to reopen ticket.");
      setTimeout(() => setError(""), 4000);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // Limit size to 2MB
        setError("Image size must be less than 2MB.");
        return;
      }
      setError("");
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, photoURL: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError("Name cannot be empty.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const capitalizeName = (name) => {
        if (!name) return "";
        return name.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
      };
      const capitalizedName = capitalizeName(formData.name);

      if (userDocId) {
        // Update existing document
        await updateDoc(doc(db, "users", userDocId), {
          name: capitalizedName,
          photoURL: formData.photoURL,
          theme: formData.theme || "dark"
        });
      } else if (currentUser?.uid) {
        // Create / merge user document in Firestore keyed by auth uid
        await setDoc(doc(db, "users", currentUser.uid), {
          name: capitalizedName,
          email: formData.email || currentUser.email,
          photoURL: formData.photoURL,
          roles: formData.roles && formData.roles.length > 0 ? formData.roles : ["Procurement"],
          theme: formData.theme || "dark"
        }, { merge: true });
        setUserDocId(currentUser.uid);
      } else {
        const docRef = await addDoc(collection(db, "users"), {
          name: capitalizedName,
          email: formData.email,
          photoURL: formData.photoURL,
          roles: ["Procurement"],
          theme: formData.theme || "dark"
        });
        setUserDocId(docRef.id);
      }

      setSuccess("Profile updated successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error saving profile:", err);
      setError("Failed to save profile. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  const initials = formData.name
    ? formData.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : "U";

  return (
    <div className="animate-fade-in" style={{ maxWidth: "800px", margin: "0 auto" }}>
      <div className="module-header">
        <div>
          <h1 className="module-title">
            <User className="sidebar-logo-icon" style={{ marginRight: "0.5rem", verticalAlign: "middle" }} />
            My Profile
          </h1>
          <p className="module-subtitle">Manage your personal information, profile photo, and security details.</p>
        </div>
      </div>

      {/* Tabs Header */}
      <div className="tabs-header" style={{ display: "flex", gap: "1rem", borderBottom: "1px solid var(--border-color)", marginBottom: "1.5rem" }}>
        <button 
          type="button"
          className={`tab-btn ${activeTab === "info" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("info");
            setSearchParams({}, { replace: true });
          }}
          style={{ 
            background: "none", 
            border: "none", 
            borderBottom: activeTab === "info" ? "2px solid var(--primary)" : "2px solid transparent",
            color: activeTab === "info" ? "var(--text-primary)" : "var(--text-secondary)",
            padding: "0.5rem 1rem", 
            cursor: "pointer",
            fontWeight: 600,
            fontSize: "0.95rem"
          }}
        >
          Profile Info
        </button>
        <button 
          type="button"
          className={`tab-btn ${activeTab === "tickets" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("tickets");
            setSearchParams({ tab: "tickets" }, { replace: true });
          }}
          style={{ 
            background: "none", 
            border: "none", 
            borderBottom: activeTab === "tickets" ? "2px solid var(--primary)" : "2px solid transparent",
            color: activeTab === "tickets" ? "var(--text-primary)" : "var(--text-secondary)",
            padding: "0.5rem 1rem", 
            cursor: "pointer",
            fontWeight: 600,
            fontSize: "0.95rem"
          }}
        >
          Support Tickets
        </button>
        <button 
          type="button"
          className={`tab-btn ${activeTab === "notifications" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("notifications");
            setSearchParams({ tab: "notifications" }, { replace: true });
          }}
          style={{ 
            background: "none", 
            border: "none", 
            borderBottom: activeTab === "notifications" ? "2px solid var(--primary)" : "2px solid transparent",
            color: activeTab === "notifications" ? "var(--text-primary)" : "var(--text-secondary)",
            padding: "0.5rem 1rem", 
            cursor: "pointer",
            fontWeight: 600,
            fontSize: "0.95rem"
          }}
        >
          Notifications
        </button>
      </div>

      {loading ? (
        <div className="card glass-card loading-state">
          <Loader2 className="animate-spin" size={32} />
          <p>Loading profile details...</p>
        </div>
      ) : activeTab === "info" ? (
        <div className="card glass-card" style={{ padding: "2.5rem" }}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1.5rem", marginBottom: "2.5rem", textAlign: "center" }}>
              {/* Profile Avatar with Hover Overlay */}
              <div style={{ position: "relative", width: "120px", height: "120px", borderRadius: "50%", overflow: "hidden", border: "2px solid var(--border-color)", boxShadow: "var(--shadow-md)" }}>
                {formData.photoURL ? (
                  <img src={formData.photoURL} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div style={{ width: "100%", height: "100%", backgroundColor: "var(--primary)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "2.5rem", color: "#ffffff" }}>
                    {initials}
                  </div>
                )}
                <label 
                  htmlFor="profilePhotoUpload" 
                  style={{ 
                    position: "absolute", 
                    bottom: 0, 
                    left: 0, 
                    right: 0, 
                    background: "rgba(0, 0, 0, 0.6)", 
                    color: "#ffffff", 
                    padding: "0.4rem 0", 
                    fontSize: "0.75rem", 
                    cursor: "pointer", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center", 
                    gap: "0.25rem",
                    transition: "var(--transition-fast)"
                  }}
                  className="photo-hover-label"
                >
                  <Upload size={12} />
                  Change
                </label>
                <input 
                  type="file" 
                  id="profilePhotoUpload" 
                  accept="image/*" 
                  onChange={handlePhotoChange} 
                  style={{ display: "none" }} 
                />
              </div>

              <div>
                <h2 style={{ fontSize: "1.5rem", fontWeight: 700, margin: 0, color: "var(--text-primary)" }}>{formData.name || "User Account"}</h2>
                <span className="text-secondary" style={{ fontSize: "0.9rem" }}>{formData.email}</span>
              </div>
            </div>

            {error && <div className="alert alert-danger" style={{ marginBottom: "1.5rem" }}>{error}</div>}
            {success && (
              <div className="alert alert-success" style={{ marginBottom: "1.5rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Check size={16} />
                <span>{success}</span>
              </div>
            )}

            <div className="grid-2" style={{ gap: "1.5rem" }}>
              <div className="form-group">
                <label className="form-label" htmlFor="fullName">Full Name</label>
                <div className="input-with-icon">
                  <User size={18} className="input-icon" />
                  <input
                    id="fullName"
                    type="text"
                    required
                    placeholder="Your Full Name"
                    className="form-input icon-padding"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="emailAddress">Email Address</label>
                <div className="input-with-icon">
                  <Mail size={18} className="input-icon" />
                  <input
                    id="emailAddress"
                    type="email"
                    disabled
                    className="form-input icon-padding"
                    value={formData.email}
                    style={{ cursor: "not-allowed", opacity: 0.7 }}
                  />
                </div>
                <p className="text-secondary" style={{ fontSize: "0.75rem", marginTop: "0.25rem" }}>Email address cannot be changed</p>
              </div>
            </div>

            <div className="form-group" style={{ marginTop: "1.5rem" }}>
              <label className="form-label" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Shield size={18} style={{ color: "var(--primary)" }} />
                Your System Roles
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "0.5rem" }}>
                {formData.roles.length > 0 ? (
                  formData.roles.map(role => (
                    <span 
                      key={role} 
                      className="sku-badge" 
                      style={{ 
                        fontSize: "0.85rem", 
                        padding: "0.4rem 0.8rem", 
                        background: "rgba(33, 150, 243, 0.15)", 
                        color: "var(--primary)",
                        borderRadius: "20px"
                      }}
                    >
                      {role}
                    </span>
                  ))
                ) : (
                  <span style={{ fontSize: "0.9rem", color: "var(--text-muted)", fontStyle: "italic" }}>No roles assigned yet. Contact your Administrator.</span>
                )}
              </div>
            </div>

            {/* Two-Factor Authentication (2FA) */}
            <div style={{ marginTop: "2rem", borderTop: "1px solid var(--border-color)", paddingTop: "1.5rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
                    <Shield size={18} style={{ color: isMfaEnabled ? "var(--success)" : "var(--primary)" }} />
                    <span style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: "0.95rem" }}>
                      Two-Factor Authentication (2FA)
                    </span>
                    <span 
                      style={{ 
                        fontSize: "0.75rem", 
                        padding: "0.15rem 0.5rem", 
                        borderRadius: "12px", 
                        fontWeight: 600,
                        backgroundColor: isMfaEnabled ? "rgba(76, 175, 80, 0.15)" : "rgba(255, 152, 0, 0.15)",
                        color: isMfaEnabled ? "var(--success)" : "#ff9800",
                        border: isMfaEnabled ? "1px solid var(--success)" : "1px solid #ff9800"
                      }}
                    >
                      {isMfaEnabled ? "Active & Enrolled" : "Not Enrolled"}
                    </span>
                  </div>
                  <p className="text-secondary" style={{ fontSize: "0.8rem", margin: 0, maxWidth: "480px" }}>
                    Protect your Jimini Logistics account using Google Authenticator or Microsoft Authenticator with standard 6-digit rolling verification codes.
                  </p>
                </div>

                <button
                  type="button"
                  className={isMfaEnabled ? "btn btn-secondary" : "btn btn-primary"}
                  onClick={() => setMfaModalOpen(true)}
                  style={{ whiteSpace: "nowrap", height: "36px", padding: "0 0.9rem", fontSize: "0.85rem" }}
                >
                  <KeyRound size={14} style={{ marginRight: "0.4rem" }} />
                  {isMfaEnabled ? "Manage 2FA" : "Setup 2FA"}
                </button>
              </div>
            </div>

            {/* Theme Preference Toggle */}
            <div style={{ marginTop: "2rem", borderTop: "1px solid var(--border-color)", paddingTop: "1.5rem" }}>
              <label className="form-label" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                System Theme
              </label>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "0.5rem" }}>
                <div>
                  <span style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: "0.95rem" }}>
                    {formData.theme === "light" ? "Light Mode" : "Dark Mode"}
                  </span>
                  <p className="text-secondary" style={{ fontSize: "0.8rem", margin: 0 }}>
                    Switch from dark mode to light mode for your personal preference.
                  </p>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={formData.theme === "light"}
                    onChange={() => setFormData(prev => ({ ...prev, theme: prev.theme === "light" ? "dark" : "light" }))}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>


            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "2.5rem", borderTop: "1px solid var(--border-color)", paddingTop: "1.5rem" }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="animate-spin" size={18} style={{ marginRight: "0.5rem" }} />
                    Saving Changes...
                  </>
                ) : (
                  <>
                    <Save size={18} style={{ marginRight: "0.5rem" }} />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      ) : activeTab === "notifications" ? (
        (() => {
          const allVisible = getVisibleNotifications(formData.roles, formData.name, true);
          const last50 = allVisible.slice(0, 50);
          return (
            <div className="card glass-card" style={{ padding: "2.5rem" }}>
              <h3 style={{ marginBottom: "0.5rem", color: "var(--primary)" }}>Recent Notifications</h3>
              <p className="text-secondary" style={{ fontSize: "0.9rem", marginBottom: "1.5rem" }}>
                Viewing the last 50 notifications targeted to your roles ({formData.roles.join(", ") || "None"}) or direct username.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {last50.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "3rem 1rem", color: "var(--text-secondary)" }}>
                    <Bell size={48} style={{ opacity: 0.3, marginBottom: "1rem" }} />
                    <h3>No Notifications</h3>
                    <p style={{ fontSize: "0.9rem" }}>No notifications have been logged for you yet.</p>
                  </div>
                ) : (
                  last50.map((notif) => {
                    const unread = !(notif.readBy || []).includes(currentUser?.uid);
                    return (
                      <div
                        key={notif.id}
                        className={`profile-notif-item ${unread ? "unread" : ""}`}
                        onClick={() => { 
                          if (unread) markAsRead(notif.id); 
                          if (isTicketNotification(notif)) {
                            const isDev = (formData.roles || []).some(r => String(r).toLowerCase() === "developer");
                            if (isDev) {
                              const targetPath = getTicketNavigationPath(notif, formData.roles);
                              navigate(targetPath);
                            } else {
                              const { ticketId, ticketNumber } = extractTicketInfo(notif);
                              setActiveTab("tickets");
                              const params = new URLSearchParams();
                              params.set("tab", "tickets");
                              if (ticketId) params.set("ticketId", ticketId);
                              if (ticketNumber) params.set("ticketNumber", ticketNumber);
                              setSearchParams(params, { replace: true });
                            }
                            return;
                          }
                          if (notif.path) {
                            navigate(notif.path);
                          }
                        }}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "1rem",
                          padding: "1rem 1.25rem",
                          borderRadius: "8px",
                          backgroundColor: unread ? "rgba(0, 224, 150, 0.03)" : "rgba(255, 255, 255, 0.01)",
                          border: unread ? "1px solid rgba(0, 224, 150, 0.15)" : "1px solid var(--border-color)",
                          cursor: unread ? "pointer" : "default",
                          transition: "all var(--transition-fast)"
                        }}
                      >
                        <div style={{ marginTop: "0.15rem" }}>
                          {notif.type === "delayed_shipping" ? (
                            <AlertTriangle size={16} style={{ color: "var(--warning)" }} />
                          ) : (
                            <Bell size={16} style={{ color: "var(--primary)" }} />
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ 
                            margin: 0, 
                            fontSize: "0.95rem", 
                            fontWeight: unread ? 600 : 400,
                            color: unread ? "var(--text-primary)" : "var(--text-secondary)" 
                          }}>
                            {notif.message}
                          </p>
                          <span className="text-muted" style={{ fontSize: "0.75rem", display: "block", marginTop: "0.25rem" }}>
                            {notif.createdAt ? new Date(notif.createdAt).toLocaleString() : "N/A"}
                          </span>
                        </div>
                        {unread && <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "var(--primary)", alignSelf: "center" }} />}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })()
      ) : (
        <div className="card glass-card" style={{ padding: "2.5rem" }}>
          {loadingTickets ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "3rem", gap: "1rem" }}>
              <Loader2 className="animate-spin text-secondary" size={32} />
              <span style={{ color: "var(--text-secondary)" }}>Loading your ticket history...</span>
            </div>
          ) : userTickets.length === 0 ? (
            <div style={{ textAlign: "center", padding: "3rem 1rem", color: "var(--text-secondary)" }}>
              <Clock size={48} style={{ opacity: 0.3, marginBottom: "1rem" }} />
              <h3>No Support Tickets</h3>
              <p style={{ fontSize: "0.9rem" }}>You haven't submitted any developer support tickets yet.</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {userTickets.map(ticket => {
                const isOpen = expandedTicketId === ticket.id;
                return (
                  <div 
                    key={ticket.id} 
                    id={`ticket-${ticket.id}`}
                    style={{ 
                      border: "1px solid var(--border-color)", 
                      borderRadius: "8px", 
                      overflow: "hidden", 
                      backgroundColor: "rgba(255, 255, 255, 0.01)" 
                    }}
                  >
                    {/* Ticket Header (Clickable Row) */}
                    <div 
                      onClick={() => handleToggleTicket(ticket)}
                      style={{ 
                        display: "flex", 
                        justifyContent: "space-between", 
                        alignItems: "center", 
                        padding: "1rem 1.25rem", 
                        cursor: "pointer", 
                        backgroundColor: "rgba(0, 0, 0, 0.15)",
                        transition: "background-color 0.2s"
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.25)"}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.15)"}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                        <span className="sku-badge" style={{ margin: 0 }}>{ticket.ticketNumber}</span>
                        <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                          {ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString() : "N/A"}
                        </span>
                        <span className={`badge ${ticket.type === "Bug" ? "badge-danger" : ticket.type === "Feature Request" ? "badge-info" : "badge-secondary"}`} style={{ margin: 0 }}>
                          {ticket.type}
                        </span>
                        {ticket.lastMessageAt && ticket.lastMessageSender !== formData.name && currentUser && !(ticket.lastMessageReadBy || []).includes(currentUser.uid) && (
                          <span 
                            className="badge badge-info" 
                            style={{ 
                              fontSize: "0.65rem", 
                              padding: "0.1rem 0.35rem", 
                              margin: 0,
                              backgroundColor: "var(--primary)",
                              color: "#ffffff"
                            }}
                          >
                            New Chat
                          </span>
                        )}
                      </div>
                      
                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                        <span 
                          className="badge" 
                          style={{ 
                            margin: 0,
                            ...getTicketStatusStyle(ticket.status)
                          }}
                        >
                          {ticket.status || "Open"}
                        </span>
                        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </div>

                    {/* Ticket Expanded Details */}
                    {isOpen && (
                      <div style={{ padding: "1.25rem", borderTop: "1px solid var(--border-color)", display: "flex", flexDirection: "column", gap: "1rem" }}>
                        <div>
                          <label className="form-label" style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>Description</label>
                          <p style={{ whiteSpace: "pre-wrap", fontSize: "0.9rem", color: "var(--text-primary)", margin: "0.25rem 0 0 0", lineHeight: 1.4 }}>
                            {ticket.description}
                          </p>
                        </div>

                        {(() => {
                          const attList = ticket.attachments && ticket.attachments.length > 0
                            ? ticket.attachments
                            : (ticket.attachment ? [ticket.attachment] : []);
                          if (attList.length === 0) return null;
                          return (
                            <div>
                              <label className="form-label" style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                                Attachments ({attList.length})
                              </label>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "0.25rem" }}>
                                {attList.map((att, idx) => (
                                  <a
                                    key={idx}
                                    href={att.data}
                                    download={att.name}
                                    className="badge badge-info"
                                    style={{ 
                                      display: "inline-flex", 
                                      alignItems: "center", 
                                      gap: "0.35rem", 
                                      textDecoration: "none", 
                                      color: "var(--primary)",
                                      border: "1px solid var(--primary)",
                                      backgroundColor: "rgba(0,0,0,0.1)",
                                      cursor: "pointer",
                                      padding: "0.3rem 0.6rem",
                                      fontSize: "0.8rem"
                                    }}
                                    title={`Download ${att.name}`}
                                  >
                                    <Download size={12} /> Download {att.name} {att.size ? `(${(att.size / 1024).toFixed(0)} KB)` : ""}
                                  </a>
                                ))}
                              </div>
                            </div>
                          );
                        })()}

                        <div style={{ borderTop: "1px solid var(--border-color)", paddingTop: "1rem", marginTop: "0.5rem" }}>
                          {ticket.status === "Complete" ? (
                            <div>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.35rem" }}>
                                <label className="form-label" style={{ display: "flex", alignItems: "center", gap: "0.3rem", color: "var(--success)", fontSize: "0.8rem", margin: 0 }}>
                                  <CheckCircle size={14} /> Developer Resolution Note
                                </label>
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", padding: "0.25rem 0.6rem", fontSize: "0.75rem" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReopenTicket(ticket);
                                  }}
                                  title="Reopen ticket back to Open status"
                                >
                                  <RotateCcw size={12} className="text-warning" /> Reopen Ticket
                                </button>
                              </div>
                              <div style={{ 
                                backgroundColor: "rgba(76, 175, 80, 0.05)", 
                                border: "1px solid rgba(76, 175, 80, 0.2)", 
                                padding: "0.75rem 1rem", 
                                borderRadius: "6px",
                                marginTop: "0.25rem",
                                fontSize: "0.9rem",
                                color: "var(--text-primary)",
                                lineHeight: 1.4
                              }}>
                                {ticket.resolutionNote || "Ticket completed successfully."}
                              </div>
                            </div>
                          ) : (
                            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", color: "var(--text-secondary)", fontSize: "0.85rem" }}>
                              <Clock size={14} className="text-secondary" />
                              <span>This ticket is currently open and pending review.</span>
                            </div>
                          )}
                        </div>

                        <TicketChat 
                          ticket={ticket} 
                          currentUserName={formData.name} 
                          currentUserRoles={formData.roles} 
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <MFAEnrollmentModal
        isOpen={mfaModalOpen}
        onClose={() => setMfaModalOpen(false)}
        userDocId={userDocId}
        userEmail={formData.email}
        isMfaEnabled={isMfaEnabled}
        onMfaStatusChange={(status) => setIsMfaEnabled(status)}
      />
    </div>
  );
}

